/**
 * Store-layer types for agent chat state.
 */

import type {ImageMediaType} from '@wandb/weave/lib/WbAgent/promptParts';

export type MessageRole = 'user' | 'assistant' | 'system';

export type ReadonlyJSONValue =
  | null
  | string
  | number
  | boolean
  | {readonly [key: string]: ReadonlyJSONValue}
  | readonly ReadonlyJSONValue[];

export type RunOutcome = 'finished' | 'errored' | 'cancelled';

/**
 * ActiveThreadLoadFailure discriminates how the active thread's load failed.
 * 'not-found' means the load returned no turns; 'error' carries a message.
 */
export type ActiveThreadLoadFailure =
  | {kind: 'error'; message: string}
  | {kind: 'not-found'};

export interface AgentChatState {
  threads: Record<string, Thread>;
  activeThreadId: string | null;
  isActiveThreadLoading: boolean;
  /**
   * activeThreadLoadFailure is the active thread's last load failure, or null
   * if the load succeeded, is in progress, or has not run.
   */
  activeThreadLoadFailure: ActiveThreadLoadFailure | null;
  /**
   * pendingRenames is the in-flight rename overlay; entries override
   * `Thread.title` for display.
   */
  pendingRenames: Record<string, {title: string}>;
  /**
   * pendingDeletes holds ids of threads currently being deleted; entries
   * hide the row from the chat history until the server confirms or the
   * mutation rolls back.
   */
  pendingDeletes: Set<string>;
  /**
   * deletedThreadIds holds ids whose server-side delete has been confirmed.
   * Sticky for the session so a late list refetch can't re-add a row.
   */
  deletedThreadIds: Set<string>;
}

export interface Thread {
  id: string;
  title: string | null;
  /**
   * loadStatus defines whether the thread is a 'summary' (metadata-only) or
   * fully-hydrated with a message tree.
   */
  loadStatus: 'summary' | 'loaded';
  /**
   * promptSnippet is the server-derived first-prompt excerpt mirrored on the
   * client. Used as a display fallback when `title` is empty.
   */
  promptSnippet: string | null;
  /**
   * serverUpdatedAt is the timestamp of the last server-canonical change
   * to `title` or `promptSnippet`; never bumped by streaming.
   */
  serverUpdatedAt: number;
  status: 'idle' | 'running' | 'error';
  error: string | null;
  rootTurnId: string | null;
  messageTree: MessageTree;
  currentRunId: string | null;
  currentMessageId: string | null;
  runOutcomes: Record<string, RunOutcome>;
  createdAt: number;
  updatedAt: number;
  /** Projects referenced by the thread's prompts; used for chip display. */
  projects: Array<{entityName: string; projectName: string}>;
}

export interface MessageTree {
  /** activeChildId maps parent IDs to their active child ID. */
  activeChildId: Record<string, string>;
  /** children maps parent IDs to their ordered child IDs. */
  children: Record<string, string[]>;
  /** currentNodeId is the latest in the active branch. */
  currentNodeId: string | null;
  /** nodes maps IDs to the message nodes. */
  nodes: Record<string, MessageNode>;
  /** parentId maps node IDs to their parent IDs (null for root-level nodes). */
  parentId: Record<string, string | null>;
  /** rootChildren lists all top-level node IDs. */
  rootChildren: string[];
}

export interface MessageNode {
  id: string;
  role: MessageRole;
  content: ContentPart[];
  createdAt: number;
}

export type ContentPart =
  | TextPart
  | ThinkingPart
  | ToolCallPart
  | ImagePart
  | WandbEntityPart
  | WandbProjectPart
  | WandbRunPart
  | WandbCallPart
  | WandbWebPageStatePart
  | UnknownPart;

export interface TextPart {
  type: 'text';
  text: string;
  status: 'in-progress' | 'complete';
}

export interface ThinkingPart {
  type: 'thinking';
  text: string;
  status: 'in-progress' | 'complete';
}

export interface ToolCallPart {
  type: 'tool-call';
  toolCallId: string;
  toolName: string;
  argsText: string;
  args: Record<string, ReadonlyJSONValue> | null;
  status: 'pending' | 'executing' | 'done' | 'error';
  result?: string;
  isError?: boolean;
}

export interface ImagePart {
  type: 'image';
  mediaType: ImageMediaType;
  base64Data: string;
}
export interface WandbEntityPart {
  type: 'wandb-entity';
  entityName: string;
}
export interface WandbProjectPart {
  type: 'wandb-project';
  entityName: string;
  projectName: string;
}

export type RunDisplayNameStatus =
  | 'pending'
  | 'resolved'
  | 'not-found'
  | 'error';
export interface WandbRunPart {
  type: 'wandb-run';
  entityName: string;
  projectName: string;
  runName: string;
  runDisplayName?: string;
  runDisplayNameStatus?: RunDisplayNameStatus;
}

export interface WandbCallPart {
  type: 'wandb-call';
  entityName: string;
  projectName: string;
  callId: string;
}

export interface WandbWebPageStatePart {
  type: 'wandb-web-page-state';
  url: string;
  entityName?: string;
  projectName?: string;
  content?: PagePromptContent[];
}

/**
 * PagePromptContent is a discriminated union of page content; grows as new page
 * kinds get added.
 */
export type PagePromptContent = WorkspacePageContent | ReportPageContent;

export interface WorkspacePageContent {
  type: 'workspace';
  viewId?: string;
  viewName?: string;
  viewType?: 'project-view' | 'sweep-view' | 'group-view';
  viewScope?: 'personal' | 'shared';
  runCountTotal?: number;
  runCountFiltered?: number;
  runCountVisualized?: number;
  runFiltersExpression?: string;
  panelSearchQuery?: string;
  runSelectorGrouping?: RunSelectorGroupingKey[];
}

export interface ReportPageContent {
  type: 'report';
  runSets: ReportRunSet[];
}

/**
 * ReportRunSet is a single run set within a report, as collected from its run
 * selector.
 */
export interface ReportRunSet {
  runCountTotal?: number;
  runCountFiltered?: number;
  runFiltersExpression?: string;
}

export interface RunSelectorGroupingKey {
  section: string;
  name: string;
}

/** Fallback for forward-compatibility with future BE part types. */
export interface UnknownPart {
  type: 'unknown';
  rawType: string;
  data: ReadonlyJSONValue;
}

export function createDefaultState(): AgentChatState {
  return {
    threads: {},
    activeThreadId: null,
    isActiveThreadLoading: false,
    activeThreadLoadFailure: null,
    pendingRenames: {},
    pendingDeletes: new Set(),
    deletedThreadIds: new Set(),
  };
}

export function createEmptyThread(id: string, now = Date.now()): Thread {
  return {
    id,
    title: null,
    status: 'idle',
    error: null,
    rootTurnId: null,
    messageTree: createEmptyTree(),
    currentRunId: null,
    currentMessageId: null,
    runOutcomes: {},
    createdAt: now,
    updatedAt: now,
    serverUpdatedAt: now,
    loadStatus: 'loaded',
    promptSnippet: null,
    projects: [],
  };
}

// ThreadSummary is a backend-derived summary from a "list turns" call.
export type ThreadSummary = Pick<
  Thread,
  | 'id'
  | 'title'
  | 'updatedAt'
  | 'createdAt'
  | 'promptSnippet'
  | 'serverUpdatedAt'
  | 'projects'
> & {
  rootTurnId: string; // Backend-derived, so `rootTurnId` definitely exists.
};

export function createThreadFromSummary(args: ThreadSummary): Thread {
  return {
    id: args.id,
    title: args.title,
    status: 'idle',
    error: null,
    rootTurnId: args.rootTurnId,
    messageTree: createEmptyTree(),
    currentRunId: null,
    currentMessageId: null,
    runOutcomes: {},
    createdAt: args.createdAt,
    updatedAt: args.updatedAt,
    serverUpdatedAt: args.serverUpdatedAt,
    loadStatus: 'summary',
    promptSnippet: args.promptSnippet,
    projects: args.projects,
  };
}

export function createEmptyTree(): MessageTree {
  return {
    nodes: {},
    parentId: {},
    children: {},
    rootChildren: [],
    currentNodeId: null,
    activeChildId: {},
  };
}

/**
 * Returns true if `parts` contains anything worth sending as a user prompt.
 * Whitespace-only text and empty-text delimiters are treated as trivial; any
 * non-text part (mention, image, etc.) is considered meaningful on its own.
 */
export function hasMeaningfulContent(parts: readonly ContentPart[]): boolean {
  return parts.some(p => p.type !== 'text' || p.text.trim().length > 0);
}
